
import { useEffect, useRef } from 'react';
import { App as AntdApp } from 'antd';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { logout } from '@/AppRedux/actions/auth';
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks';

export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { notification } = AntdApp.useApp();
  const dispatch = useAppDispatch();
  const { token, propCode: authPropCode } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const { propcode } = useParams();
  
  const hasHandledPropcodeMismatchRef = useRef(false);
  const normalizedRoutePropcode = propcode?.trim() ?? null;
  const normalizedAuthPropcode = authPropCode?.trim() ?? null;
  const isPropcodeMismatch = Boolean(
    token &&
    normalizedRoutePropcode &&
    normalizedAuthPropcode &&
    normalizedAuthPropcode !== normalizedRoutePropcode,
  );

  useEffect(() => {
    if (!isPropcodeMismatch || hasHandledPropcodeMismatchRef.current) {
      return;
    }

    hasHandledPropcodeMismatchRef.current = true;

    notification.warning({
      message: 'Property Code Changed',
      description: `Detected a property code change from ${normalizedAuthPropcode} to ${normalizedRoutePropcode}. Please sign in again.`,
      placement: 'topRight',
    });

    dispatch(logout());
  }, [
    dispatch,
    isPropcodeMismatch,
    normalizedAuthPropcode,
    normalizedRoutePropcode,
    notification,
  ]);

  if (isPropcodeMismatch) {
    return null;
  }

  if (!token) {
    return <Navigate to={`/administrator/${propcode}/login`} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
